import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import debounce from 'lodash/debounce';

// Hooks
import { useQueryParam } from 'Shared/utils/hooks';

// Types
import { UpdateAiReportType } from 'Shared/types/UpdateAiReportType.type';

// Utils
import { getCurrentMoment } from 'Shared/utils/DateTime.utils';
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Selectors
import { getUpdateAiReportBySlug, getCurrentlyGeneratingSlugs } from 'Shared/selectors/UpdateAiSettings.selectors';

// Actions
import {
  createUpdateAiReportSections,
  cancelGeneratingAiReportSections,
  updateUpdateAiReport,
  fetchUpdateAiReport,
  deleteUpdateAiReport,
} from 'Shared/actions/UpdateAiReports.actions';

export type handleUpdateAiReportProps = {
  name?: string;
  body?: Array<{
    template_section_slug: string;
    section_body?: string;
  }>;
};

type Props = {
  slug: string;
  withFetch?: boolean;
};

export const useUpdateAiReport = ({ slug, withFetch = false }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [generateModeParam] = useQueryParam('generate_mode');
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const report: UpdateAiReportType = useSelector(state => getUpdateAiReportBySlug(state, { slug }))?.toJS();
  const currentlyGeneratingSlugs = useSelector(state => getCurrentlyGeneratingSlugs(state)).toJS();

  const isGenerating = currentlyGeneratingSlugs.length > 0;
  const hasGeneratedSections =
    report?.body && report?.body?.length !== report?.body.filter(section => section.state === 'draft').length;

  const handleGenerateReport = ({
    startDate,
    endDate,
    templateSectionSlugs,
  }: {
    startDate: string;
    endDate: string;
    templateSectionSlugs: string[];
  }) => {
    dispatch(
      createUpdateAiReportSections({
        slug,
        data: { startDate, endDate, templateSectionSlugs },
        // @ts-ignore
        params: { generateMode: generateModeParam },
      })
    );
  };

  const handleCancelGenerateReport = ({ templateSectionSlugs }: { templateSectionSlugs: string[] }) => {
    dispatch(
      cancelGeneratingAiReportSections({
        slug,
        data: { templateSectionSlugs },
      })
    );
  };

  const handlePublishReport = () => {
    dispatch(
      updateUpdateAiReport({
        slug: slug,
        data: { publishedAt: getCurrentMoment().format() },
      })
    ).then(response => {
      if (response.status === 200) {
        history.push(PageUrlsUtil.makeUpdateAiRportPageUrl({ slug }));
      }
    });
  };

  const debouncedUpdate = useRef(
    debounce(data => {
      dispatch(
        updateUpdateAiReport({
          slug,
          data,
        })
      ).then(() => setIsUpdating(false));
    }, 250)
  ).current;

  const handleUpdateAiReport = ({ name, body }: handleUpdateAiReportProps) => {
    setIsUpdating(true);
    debouncedUpdate({ name, body });
  };

  const handleDeleteReport = () => {
    return dispatch(deleteUpdateAiReport({ slug }));
  };

  useEffect(() => {
    if (slug && withFetch) {
      setIsFetching(true);
      dispatch(fetchUpdateAiReport({ slug })).finally(() => {
        setIsFetching(false);
      });
    }
  }, []);

  return {
    report,
    isGenerating,
    isFetching,
    isUpdating,
    currentlyGeneratingSlugs,
    hasGeneratedSections,
    handlePublishReport,
    handleUpdateAiReport,
    handleGenerateReport,
    handleCancelGenerateReport,
    handleDeleteReport,
  };
};
